(() => {
  const MENU_SELECTOR = '.instant-search-menu';
  const PRODUCT_BLOCK_SELECTOR = '.block-products';
  const PRODUCT_ITEM_SELECTOR = '.cs-item';
  const QUICK_DESCRIPTION_SELECTOR = '[data-xcloud-quick-description]';
  const CONTEXTUAL_PRICES_SELECTOR = '[data-xcloud-contextual-prices]';

  const productDataCache = new Map();
  const productDataRequests = new Map();

  const findDetailsForItem = (menu, item) => {
    const itemId = item.dataset.id;

    if (!itemId) return null;

    return Array.from(menu.querySelectorAll(`${PRODUCT_BLOCK_SELECTOR} dd[data-id]`)).find(
      (details) => details.dataset.id === itemId
    );
  };

  const addInlinePrice = (menu, item) => {
    if (item.querySelector('.xcloud-search-inline-price')) return;

    const details = findDetailsForItem(menu, item);
    const itemDetails = item.querySelector('.cs-item-details');
    const sourcePrices = details?.querySelectorAll('.details-top-text > .price');

    if (!itemDetails || !sourcePrices?.length) return;

    const priceBlock = document.createElement('div');
    priceBlock.className = 'xcloud-search-inline-price';
    priceBlock.setAttribute('aria-hidden', 'true');

    sourcePrices.forEach((price) => priceBlock.appendChild(price.cloneNode(true)));
    itemDetails.appendChild(priceBlock);
  };

  const buildDescriptionUrl = (productUrl) => {
    const url = new URL(productUrl, window.location.origin);

    if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return null;

    url.search = '';
    url.searchParams.set('section_id', 'xcloud-search-product-data');

    return url.toString();
  };

  const requestProductData = (productUrl) => {
    const requestUrl = buildDescriptionUrl(productUrl);

    if (!requestUrl) return Promise.resolve({ description: '', priceMarkup: '' });
    if (productDataCache.has(requestUrl)) return Promise.resolve(productDataCache.get(requestUrl));
    if (productDataRequests.has(requestUrl)) return productDataRequests.get(requestUrl);

    const request = fetch(requestUrl, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Search product data request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const description = documentFragment.querySelector(QUICK_DESCRIPTION_SELECTOR)?.textContent?.trim() || '';
        const priceMarkup = documentFragment.querySelector(CONTEXTUAL_PRICES_SELECTOR)?.innerHTML?.trim() || '';
        const productData = { description, priceMarkup };

        productDataCache.set(requestUrl, productData);
        productDataRequests.delete(requestUrl);

        return productData;
      })
      .catch(() => {
        productDataRequests.delete(requestUrl);
        return { description: '', priceMarkup: '' };
      });

    productDataRequests.set(requestUrl, request);
    return request;
  };

  const replaceContextualPrices = (menu, item, details, priceMarkup) => {
    const detailsPriceContainer = details?.querySelector('.details-top-text');
    const itemPriceContainer = item?.querySelector('.cs-price-block');

    if ((!detailsPriceContainer && !itemPriceContainer) || !priceMarkup) return false;

    const buildContextualPrices = () => {
      const template = document.createElement('template');
      template.innerHTML = priceMarkup;

      return Array.from(template.content.children).filter((element) => element.classList.contains('price'));
    };

    let replacedPrices = false;

    if (detailsPriceContainer) {
      const contextualPrices = buildContextualPrices();

      if (contextualPrices.length) {
        Array.from(detailsPriceContainer.children)
          .filter((element) => element.classList.contains('price'))
          .forEach((element) => element.remove());

        contextualPrices.forEach((price) => detailsPriceContainer.appendChild(price));
        replacedPrices = true;
      }
    }

    if (itemPriceContainer) {
      const contextualPrices = buildContextualPrices();

      if (contextualPrices.length) {
        const accessiblePriceId = itemPriceContainer.querySelector('.price[id]')?.id;

        Array.from(itemPriceContainer.children)
          .filter((element) => element.classList.contains('price'))
          .forEach((element) => element.remove());

        if (accessiblePriceId) contextualPrices[0].id = accessiblePriceId;
        contextualPrices.forEach((price) => itemPriceContainer.appendChild(price));
        replacedPrices = true;
      }
    }

    item.querySelector('.xcloud-search-inline-price')?.remove();
    if (!itemPriceContainer) addInlinePrice(menu, item);

    return replacedPrices;
  };

  const hydrateProductData = (menu, item, details, productUrl) => {
    const currentDescription = details?.querySelector('.description');

    if (!item || !productUrl) return;
    if (item.dataset.xcloudProductDataUrl === productUrl) return;

    const fallbackDescription = currentDescription?.textContent?.trim() || '';

    item.dataset.xcloudProductDataUrl = productUrl;
    if (details) details.dataset.xcloudProductDataUrl = productUrl;
    item.classList.add('xcloud-search-prices-pending');
    item.setAttribute('aria-busy', 'true');
    details?.classList.add('xcloud-search-prices-pending');
    details?.setAttribute('aria-busy', 'true');

    if (currentDescription) {
      currentDescription.textContent = '';
      currentDescription.classList.add(
        'xcloud-search-quick-description',
        'xcloud-search-quick-description--loading'
      );
      currentDescription.setAttribute('aria-busy', 'true');
    }

    requestProductData(productUrl).then(({ description, priceMarkup }) => {
      if (item.dataset.xcloudProductDataUrl !== productUrl) return;

      if (currentDescription) {
        currentDescription.textContent = description || fallbackDescription;
        currentDescription.classList.remove('xcloud-search-quick-description--loading');
        currentDescription.removeAttribute('aria-busy');
      }

      replaceContextualPrices(menu, item, details, priceMarkup);
      item.classList.remove('xcloud-search-prices-pending');
      item.removeAttribute('aria-busy');
      details?.classList.remove('xcloud-search-prices-pending');
      details?.removeAttribute('aria-busy');
    });
  };

  const prepareItemData = (menu, item) => {
    const details = findDetailsForItem(menu, item);
    const productLink =
      details?.querySelector('.cs-product-link[href]') ||
      (item.matches('a[href]') ? item : item.querySelector('.cs-product-link[href]'));

    hydrateProductData(menu, item, details, productLink?.href);
  };

  const updateActiveProductData = (menu) => {
    const activeDetails = menu.querySelector(`${PRODUCT_BLOCK_SELECTOR} dd.active`);

    if (!activeDetails) return;

    const activeItem = Array.from(
      menu.querySelectorAll(`${PRODUCT_BLOCK_SELECTOR} ${PRODUCT_ITEM_SELECTOR}[data-id]`)
    ).find((item) => item.dataset.id === activeDetails.dataset.id);
    const productLink = activeDetails.querySelector('.cs-product-link[href]');
    const productUrl = productLink?.href;

    hydrateProductData(menu, activeItem, activeDetails, productUrl);
  };

  const enhanceMenu = (menu) => {
    menu.querySelectorAll(`${PRODUCT_BLOCK_SELECTOR} ${PRODUCT_ITEM_SELECTOR}`).forEach((item) => {
      addInlinePrice(menu, item);
      prepareItemData(menu, item);
    });

    updateActiveProductData(menu);
  };

  const observedMenus = new WeakSet();
  const pendingMenus = new Set();
  let animationFrame = null;
  const scheduleEnhancement = (menu) => {
    if (menu) pendingMenus.add(menu);
    if (animationFrame !== null) return;

    animationFrame = window.requestAnimationFrame(() => {
      animationFrame = null;
      pendingMenus.forEach((pendingMenu) => {
        if (pendingMenu.isConnected) enhanceMenu(pendingMenu);
      });
      pendingMenus.clear();
    });
  };

  const observeMenu = (menu) => {
    if (!menu || observedMenus.has(menu)) return;
    observedMenus.add(menu);

    const menuObserver = new MutationObserver(() => scheduleEnhancement(menu));
    menuObserver.observe(menu, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    enhanceMenu(menu);
  };

  const findMenus = (scope = document) => {
    if (scope.matches?.(MENU_SELECTOR)) observeMenu(scope);
    scope.querySelectorAll?.(MENU_SELECTOR).forEach(observeMenu);
  };

  const start = () => {
    findMenus();

    const discoveryObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) findMenus(node);
        });
      });
    });
    discoveryObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
